/* eslint-disable react/no-unescaped-entities */
'use client';

import React from 'react';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container } from '@layout';
import {Accordion, Heading} from '@ui';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';
import { t as translate } from '@utils/translate';

interface AccordionDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

type LifecycleItem = {
  id: string;
  panelId: string;
  title: string;
  content: string;
  defaultOpen?: boolean;
};

function isLifecycleItem(value: unknown): value is LifecycleItem {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.panelId === 'string' &&
    typeof v.title === 'string' &&
    typeof v.content === 'string'
  );
}

const AccordionDemo: React.FC<AccordionDemoProps> = ({
  isSpanish = false,
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
}) => {
  const locale = isSpanish ? 'es' : 'en';
  const tc = (en: string, es: string) => (isSpanish ? es : en);

  const shortItemsRaw = translate('accordion_demo.short_items', 'design-system', locale) as unknown;
  const fromShort = Array.isArray(shortItemsRaw) ? shortItemsRaw.filter(isLifecycleItem) : [];

  const itemsRaw =
    fromShort.length > 0
      ? fromShort
      : ((translate('development_process.lifecycle_items', 'design-system', locale) as unknown) ??
          (translate('accordion_demo.lifecycle_items', 'design-system', locale) as unknown));

  const itemsRawFallback =
    Array.isArray(itemsRaw) ? itemsRaw : translate('accordion_demo.lifecycle_items', 'design-system', locale);

  const accordionData: LifecycleItem[] = Array.isArray(itemsRawFallback)
    ? itemsRawFallback.filter(isLifecycleItem)
    : [];

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as="h3">{tc('Accordion', 'Acordeón')}</Heading>
        <p className='sub full'>
          {tc(
            'One panel at a time (type="accordion", the default). Opening another header closes the previous. Click the open header to collapse all.',
            'Un panel a la vez (type="accordion", el valor predeterminado). Abrir otro encabezado cierra el anterior. Haz clic en el encabezado abierto para cerrar todos.'
          )}
        </p>
        <Accordion id='acc-demo' data={accordionData.length ? accordionData : []} />
        <Heading as="h3" id='collapsible'>
          {tc('Collapsible', 'Plegable')}
        </Heading>
        <p className='sub full'>
          {tc(
            'Any number of panels open (type="collapsible", or the Collapsible alias). Each header toggles only its own panel.',
            'Cualquier número de paneles abiertos (type="collapsible", o el alias Collapsible). Cada encabezado alterna solo su propio panel.'
          )}
        </p>
        <Accordion
          type='collapsible'
          id='acc-demo-collapsible'
          data={
            accordionData.length
              ? accordionData.map((item, i) => ({
                  ...item,
                  id: `${item.id}-c`,
                  panelId: `${item.panelId}-c`,
                  defaultOpen: i < 2,
                }))
              : []
          }
        />
        <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<div class="accordion" id="acc-demo">
  <div class="card acc-item is-open">
    <h3 class="acc-header">
      <button class="acc-trigger" type="button" aria-expanded="true">
        Title
        <span class="acc-indicator" aria-hidden="true"></span>
      </button>
    </h3>
    <div class="acc-panel">
      <div class="acc-panel-inner">Panel content</div>
    </div>
  </div>
</div>

<div class="accordion collapsible">
  <div class="card acc-item is-open">
    <h3 class="acc-header">
      <button class="acc-trigger" type="button" aria-expanded="true">Title</button>
    </h3>
    <div class="acc-panel"><div class="acc-panel-inner">Panel content</div></div>
  </div>
</div>`}
        />
      </Container>
    </Section>
  );
};

export default AccordionDemo;
